import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { Link } from "react-router-dom";
import axios from "axios";
import { omit } from "lodash";
import moment from "moment";
import type { Moment } from "moment";
import { Button, Col, Divider, Form, Modal, Row, Space, Steps, Tooltip, message } from "antd";
import { DataNode } from "antd/lib/tree";
import { LeftOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../..";
import type { IProductCreateContext, RequestParams } from "./utils/types";
import {
  ProductParams,
  ProductSelectAgreement,
  ProductOfferDetailsModal,
} from "../../../components/ProductCreate";
import type {
  Agreement,
  Offer,
  ServiceRate,
  AggregationRule,
  IRuleCondition,
  IChangingRecordKey,
} from "../../../lib/types";
import type { IOfferHierarchy } from "../../../store/slices/offering/types";
import {
  fetchCurrentOfferHierarchy,
  fetchHierarchyOfferList,
  offeringActions,
} from "../../../store/slices/offering/offering";
import {
  geDicValueById,
  infinityDateHttpFormat,
  round,
  toDisplayFormat,
  toHttpFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
  useActiveModal,
} from "../../../lib/utils";
import { useProductTableData } from "./utils/useProductTableData";
import useDebounce from "../../../lib/hooks/useDebounce";
import { fetchTax, taxTsActions } from "../../../store/slices/taxTs";
import { createProductRoute } from "../../../lib/routes/products";
import { ProductsCreateInvoicing } from "../../../components/ProductCreate/ProductsCreateInvoicing/ProductsCreateInvoicing";
import { addBill, fetchBillsByAggId } from "../../../store/slices/bills";
import {
  fetchBillStatus,
  fetchBillType,
  fetchPayStatus,
  fetchServiceStatus,
} from "../../../store/slices/rtkDictionaries";
import { formatKey } from "../../../components/ProductCreate/utils/formatKey";
import { OfferList } from "../../../components/OfferList/OfferList";
import { useOfferList } from "../../../components/OfferList/utils/useOfferList";
import { FormInfo } from "../../../components/common/FormInfo/FormInfo";
import { OFFER_STATUSES } from "../../../store/slices/offering/const";
import { getRuleConditionBody } from "../../OfferContainer/utils/getOfferCreateBody/getRuleConditionBody";
import { hasAccess } from "../../../components/RolesModule/hasAccess";
import { MENU_ACCOUNT_BILLS_WRITE } from "../../../lib/const/permissions";
import "./ProductCreateContainer.less";

// Создаваемый ruleCondition (для передачи в форму)
const newRuleCondition: IRuleCondition = {
  direction: "",
  relation: "OR",
};

export const ProductCreateContainer = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const history = useHistory();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();

  const { offerHierarchy, serviceTypeList } = useAppSelector((state) => state.offering);

  const offer = useAppSelector((state) => state.offering.offer) as Offer<string>;
  const hierarchyOfferList = useAppSelector((state) => state.offering.hierarchyOfferList);

  const agreements = useAppSelector((state) => state.agreements.agreements) as Agreement[];
  const { tax } = useAppSelector((state) => state.taxTs);
  const { tenantProperties } = useAppSelector((state) => state.tenants);

  // В России редактируется цена без налога, иначе - с. Флаг об этом
  const isEditPriceWithVat = useMemo(() => (tenantProperties as any)?.inputTax === "true", [
    tenantProperties,
  ]);

  const { activeModal, setActiveModal, activeModalOptions } = useActiveModal<"offerDetails" | "">(
    ""
  );

  const [addedOfferProductsParams, setAddedOfferProductsParams] = useState<
    IOfferHierarchy | Offer
  >();

  const [treeDataFormat, setTreeDataFormat] = useState<DataNode[]>([]);

  // Очистка
  useEffect(() => {
    return () => {
      dispatch(offeringActions.cleanHierarchyOfferList());
      dispatch(offeringActions.cleanOfferHierarchy());
      setTreeDataFormat([]);
    };
  }, []);

  // [ключ редактируемой строки, mode]
  const [changingRecordKey, setChangingRecordKey] = useState<IChangingRecordKey>();

  // Используется только для целей валидации информации по продукту, данные хранятся/изменяются в tableData
  const [createForm] = Form.useForm<any>();

  // Защита от двойного нажатия при создании
  const [isProductCreating, setIsProductCreating] = useState(false);

  const [selectedAgreement, setSelectedAgreement] = useState<Agreement | undefined>();

  const offerListProps = useOfferList({
    initialOfferListViewMode: "createProduct",
    initialSearchValues: {
      isStandalone: true,
      onDate: moment(),
      offeringStatusId: OFFER_STATUSES.ACTIVE,
      page: 0,
      size: 20,
    },
  });

  // Флаг валидации оффера для отработки попытки создания сразу без заполения деталей оффера
  const [isCreatedOfferValid, setIsCreatedOfferValid] = useState(false);

  // Перевод флага валидации в true для режима создания "выбрать", т.к. изменения не предполагаются
  useEffect(() => {
    setIsCreatedOfferValid(offerListProps.offerListSelectMode === "select");
  }, [offerListProps.offerListSelectMode]);

  const serviceType = useMemo(() => {
    const type = geDicValueById(
      serviceTypeList,
      offerListProps.searchListValues.serviceTypeId || offerListProps.selectedOffer?.serviceTypeId
    );
    return type;
  }, [
    serviceTypeList,
    offerListProps.searchListValues.serviceTypeId,
    offerListProps.selectedOffer,
  ]);

  // Создаваемый aggregationRule (для передачи в форму)
  const getNewAggregationRule = (): AggregationRule<"createForm"> => ({
    id: Date.now(),
    ruleCondition: [newRuleCondition],
  });

  const emptyOffering = useMemo((): Offer<string> => {
    const fromDate = offerListProps.searchListValues.onDate
      ? toHttpFormat(offerListProps.searchListValues.onDate)
      : "";
    const toDate = infinityDateHttpFormat;
    const extName = serviceType?.name || "";

    return {
      extName,
      lifespanPeriodId: 1,
      lifespanPeriodQty: 1,
      offeringStatusId: OFFER_STATUSES.ACTIVE,
      offeringTypeId: 3,
      prolongTypeId: 1,
      fromDate,
      toDate,
      serviceTypeId: serviceType?.id,
      serviceType,
      aggregationRules: [],
      serviceRates: [],
      tariffModelId: serviceType?.serviceModeId === 3 ? undefined : 1,
      serviceProfile: {
        fromDate,
        toDate,
        profileTypeId: 1,
      },
    };
  }, [offerListProps.searchListValues.onDate, serviceType]);

  const { onChangeTableData, onChangeTableOfferData, tableData, formFields } = useProductTableData({
    initialFromDate: offerListProps.searchListValues.onDate,
    initialQuantity: 1,
    agreementId: selectedAgreement?.id || 0, // нужен обязательно, как сделать приличнее пока не придумалось
    offerListSelectMode: offerListProps.offerListSelectMode,
    hierarchyOfferList,
    offer: offerListProps.offerListSelectMode === "new" ? emptyOffering : offer,
    offerHierarchy,
    treeDataFormat,
    setTreeDataFormat,
  });

  // При обновлении taxRate пересчитываем в таблице (так как иначе сброситься всё в 0)
  useEffect(() => {
    if (tableData[0]?.key === "") {
      const serviceRates = tableData[0]?.offering?.serviceRates;
      const taxRate = tax?.taxRate || "0";
      const taxMultiplier = Number(taxRate) / 100 + 1;

      if (Array.isArray(serviceRates) && serviceRates.length > 0) {
        const updatedServiceRates = serviceRates.map((rateItem) => {
          const price = isEditPriceWithVat
            ? round(Number(rateItem.priceWithVat) / taxMultiplier)
            : rateItem.price;
          const priceWithVat = isEditPriceWithVat
            ? rateItem.priceWithVat
            : round(Number(rateItem.price) * taxMultiplier);
          return {
            ...rateItem,
            taxRate,
            price,
            priceWithVat,
          };
        });

        onChangeTableOfferData({
          key: tableData[0].key,
          fieldName: "serviceRates",
          value: updatedServiceRates,
        });
      }
    }
  }, [tax, serviceType]);

  const [step, setStep] = useState<IProductCreateContext["step"]>(0);

  // Уведомление при входе в параметры предолжения
  useEffect(() => {
    if (step === 2 && offerListProps.offerListSelectMode !== "select") {
      message.warning({
        content: "Пожалуйста, проверьте атрибуты Предложения",
        duration: 3,
        style: { marginTop: "100px" },
      });
    }
  }, [offerListProps.offerListSelectMode, step]);

  const [treeExpandedKeys, setTreeExpandedKeys] = useState<string[]>([]);

  // Получение списка id офферов, вложенных в иерархию (на всех уровнях)
  const getOfferHierarchyIds = (hierarchy: IOfferHierarchy): number[] => {
    const ids: number[] = [hierarchy.id];
    const addIds = ({ children }: Partial<IOfferHierarchy>) => {
      if (!children) return;
      children.forEach((item) => {
        if (item.id && !ids.includes(item.id)) {
          ids.push(item.id);
        }
        addIds(item);
      });
    };
    addIds(hierarchy);
    return ids;
  };

  // Объект создаваемого продукта
  const getProductCreatingBody = () => {
    const productFromDate = toHttpFormatStartOfDay(tableData[0].fromDate);
    const productToDate = tableData[0].toDate
      ? toHttpFormatEndOfDay(tableData[0].toDate)
      : undefined;
    const getRulesAndRates = (offering: Offer<moment.Moment, "createForm">) => {
      const serviceRates: ServiceRate<string>[] = [];
      const trafficRates: ServiceRate<string>[] = [];
      const aggregationRules: AggregationRule<"createObj">[] = [];
      const hideTsError = offering.serviceRates
        ?.map((rate) => ({ ...rate, fromDate: productFromDate }))
        ?.forEach((rate) => {
          const {
            id,
            balanceTypeId,
            priceWithVat,
            toDate,
            offeringId,
            aggregationRuleId,
            ...restRate
          } = rate;
          if (aggregationRuleId) {
            trafficRates.push({ ...restRate, aggregationRuleId });
          } else {
            serviceRates.push(restRate);
          }
        });
      const hideTsError1 = offering.aggregationRules
        ?.map((rule) => ({
          ...rule,
          fromDate: productFromDate,
          ruleCondition: getRuleConditionBody(rule.ruleCondition),
        }))
        ?.forEach((rule) => {
          const { id, toDate, aggName, periodTypeId, rangeFrom, ...restRule } = rule;
          // Указаны утверждения у aggName, periodTypeId, rangeFrom, т.к. в форме обязательны
          aggregationRules.push({
            ...restRule,
            aggName: aggName!,
            periodTypeId: periodTypeId!,
            rangeFrom: rangeFrom!,
            serviceRates: trafficRates
              .filter(({ aggregationRuleId }) => aggregationRuleId === id)
              .map(({ aggregationRuleId, ...restRule }) => restRule),
          });
        });
      return { serviceRates, aggregationRules };
    };

    const getChildrenProducts = (tableRecordKey: string) => {
      // Отбор непосредственных потомков
      const directChildren = tableData.filter(({ key, parentId }) => {
        const rootLevelNumber = formatKey(tableRecordKey).split("-").length; // Уровень нахождения родителя в дереве
        const childLevelNumber = formatKey(key).split("-").length; // Уровень нахождения ребенка в дереве
        const isDirectChild =
          String(parentId)?.startsWith(tableRecordKey) && childLevelNumber === rootLevelNumber + 1; // Продукт является непосредственным потомком текущего, если находится на 1 уровень ниже и имеет ключ, начинающийся с ключа родителя
        return isDirectChild;
      });
      if (directChildren.length === 0) {
        return null;
      }

      return directChildren.map((child) => {
        const { aggregationRules, serviceRates } = getRulesAndRates(child.offering);

        const offering =
          offerListProps.offerListSelectMode === "select"
            ? { id: child.offering.id }
            : {
                ...omit(child.offering, "id", "aggregationRules"),
                fromDate: productFromDate,
                toDate: infinityDateHttpFormat,
                aggregationRules,
                serviceRates,
                serviceProfile: {
                  ...omit(child.offering.serviceProfile, "id"),
                  fromDate: productFromDate,
                  toDate: infinityDateHttpFormat,
                },
              };
        return {
          ...omit(child, "id", "key", "parentId", "prolongOffer"),
          quantity: child.quantity,
          agreementId: selectedAgreement?.id,
          offering,
          serviceTypeId: child.offering.serviceType?.id,
          fromDate: child.fromDate ? toHttpFormatStartOfDay(child.fromDate) : undefined,
          toDate: child.toDate ? toHttpFormatEndOfDay(child.toDate) : undefined,
          children: getChildrenProducts(child.key),
          prolongOfferingId: child.prolongOffer?.id,
        };
      });
    };

    // console.log({ offerListSelectMode: offerListProps.offerListSelectMode });

    const children = getChildrenProducts(tableData[0].key);

    const serviceTypeId = tableData[0].offering.serviceType?.id;

    switch (offerListProps.offerListSelectMode) {
      case "basedOn":
      case "new": {
        const createdServiceProfile = {
          ...omit(tableData[0].offering.serviceProfile, "id"),
          name: `Для предложения ${tableData[0].offering.extName}`,
          fromDate: productFromDate,
          toDate: infinityDateHttpFormat,
        };

        const { aggregationRules, serviceRates } = getRulesAndRates(tableData[0].offering);

        const createdOffering = {
          ...omit(tableData[0].offering, "id", "aggregationRules"),
          agreementId: selectedAgreement?.id,
          fromDate: productFromDate,
          toDate: infinityDateHttpFormat,
          serviceTypeId,
          serviceProfile: createdServiceProfile,
          aggregationRules,
          serviceRates,
        };

        if (!tableData[0].offering.name) {
          delete createdOffering.name;
        }

        return {
          ...omit(tableData[0], "id", "key", "parentId", "name", "extName", "prolongOffer"),
          quantity: tableData[0].quantity,
          agreementId: selectedAgreement?.id,
          offering: createdOffering,
          serviceProfile: createdServiceProfile,
          children,
          fromDate: productFromDate,
          toDate: productToDate,
          serviceTypeId,
          prolongOfferingId: tableData[0].prolongOffer?.id,
        };
      }
      case "select":
        return {
          ...omit(tableData[0], "id", "key", "parentId", "name", "extName", "prolongOffer"),
          quantity: tableData[0].quantity,
          agreementId: selectedAgreement?.id,
          offering: { id: tableData[0].id },
          serviceProfile: { id: tableData[0].offering.serviceProfile.id },
          children,
          fromDate: productFromDate,
          toDate: productToDate,
          serviceTypeId,
          prolongOfferingId: tableData[0].prolongOffer?.id,
        };
      default:
        return {};
    }
  };

  // Создание продукта
  const createProduct = async () => {
    const url = createProductRoute();
    const creatingBody = getProductCreatingBody();
    // return console.log({ creatingBody });
    // console.log({ offering: tableData[0].offering });
    // const { offeringTypeId, offeringStatusId } = tableData[0].offering;
    // if (offeringTypeId === 1) {
    //   const baseData = {
    //     dependencyCheck: true,
    //     offeringId: offerListProps.selectedOffer?.id!,
    //     agreementId: selectedAgreement?.id!,
    //     onDate: toHttpFormatStartOfDay(tableData[0].fromDate),
    //   };
    //   const checkData =
    //     offeringStatusId === OFFER_STATUSES.ACTIVE ? { ...baseData, offeringStatusId } : baseData;
    //   try {
    //     const validationResult = await validateOfferAvailibility(checkData);
    //     const { validationStatusId, availableOffering, resultList } = validationResult;
    //     if (validationStatusId === 100 && availableOffering) {
    //       return axios.post(url, creatingBody);
    //     } else {
    //       Modal.error({
    //         content: resultList[0]?.message || "Предложение не доступно к подключению",
    //       });
    //       return Promise.reject();
    //     }
    //   } catch (e) {
    //     Modal.error({
    //       content:
    //         "При проверке Предложения произошла ошибка. Пожалуйста, повторите попытку позднее",
    //     });
    //     return Promise.reject();
    //   }
    // } else {
    return axios.post(url, creatingBody);
    // }
  };

  const [productIdsCreate, setProductIdsCreate] = useState<number[]>([]);

  const getProductsChildrenCreateIds = (childProduct) => {
    childProduct.forEach((product) => {
      setProductIdsCreate((prev) => [...prev, product?.id]);
      if (Array.isArray(product?.children)) {
        getProductsChildrenCreateIds(product?.children);
      }
    });
  };

  const [savedStepSecondOnDateValue, setSavedStepSecondOnDateValue] = useState<Moment>();

  const getOfferByIds = ({
    id,
    onDate,
    dataForOfferId,
  }: {
    id: number[];
    onDate: string;
    dataForOfferId?: {
      isAddOffer: boolean;
      selectedOfferId: number;
    };
  }) => {
    const params = {};

    // У текущего оффера может не быть иерархии, а список hierarchyOfferList нужен для формирования таблицы
    dispatch(
      fetchHierarchyOfferList({
        id,
        onDate,
        dataForOfferId,
      })
    );
  };

  const getCurrentOfferHierarchy = ({ onDateValue }: RequestParams) => {
    const { selectedOffer } = offerListProps;
    const selectedOfferId = selectedOffer && selectedOffer.id;

    if (selectedOfferId && selectedOffer) {
      if (onDateValue) {
        offerListProps.onChangeSearchValue({ onDate: onDateValue });
      }

      let onDate = moment(selectedOffer.fromDate).isAfter(moment(), "day")
        ? selectedOffer?.fromDate || ""
        : toHttpFormatStartOfDay(offerListProps.searchListValues.onDate);

      if (onDateValue) {
        onDate = (onDateValue && toHttpFormatStartOfDay(onDateValue)) || "";
      }

      if (selectedOffer.bundleTypeId === 1) {
        getOfferByIds({
          id: [selectedOfferId],
          onDate,
          dataForOfferId: { isAddOffer: true, selectedOfferId },
        });
      } else {
        dispatch(fetchCurrentOfferHierarchy({ id: selectedOfferId, onDate })).then(
          ({ payload }) => {
            const offerIds = payload
              ? getOfferHierarchyIds(payload as IOfferHierarchy)
              : [selectedOfferId];
            // У текущего оффера может не быть иерархии, а список hierarchyOfferList нужен для формирования таблицы
            getOfferByIds({
              id: offerIds,
              onDate,
              dataForOfferId: { isAddOffer: true, selectedOfferId },
            });
          }
        );
      }
    }
  };

  const [formCreateInvoicing] = Form.useForm<any>();

  const nextStep: IProductCreateContext["nextStep"] = () => {
    switch (step) {
      case 0:
        setStep(1);
        break;
      case 1: {
        const { selectedOffer } = offerListProps;
        const selectedOfferId = selectedOffer && selectedOffer.id;
        if (offerListProps.offerListSelectMode === "new") {
          setStep(2);
          break;
        }
        if (!selectedOfferId || !selectedOffer) {
          break;
        }

        getCurrentOfferHierarchy({});

        setSavedStepSecondOnDateValue(offerListProps.searchListValues.onDate);

        setStep(2);
        break;
      }
      case 2:
        // TODO Evgeniy Sumaev: проверка заполненности скрытых строк не происходит. Реализовать на основании данных таблицы
        if (!isCreatedOfferValid) {
          Modal.warning({
            content: "Пожалуйста, заполните необходимые параметры для индивидуального предложения",
          });
          break;
        }
        createForm.validateFields().then(() => {
          setIsProductCreating(true);
          createProduct()
            .then((res) => {
              if (
                tableData[0].offering?.serviceProfile?.paymentTypeId === 2 ||
                !hasAccess(MENU_ACCOUNT_BILLS_WRITE)
              ) {
                history.push(pathname.replace("/create", ""));
              } else {
                setProductIdsCreate([res?.data?.id]);

                if (Array.isArray(res?.data?.children)) {
                  getProductsChildrenCreateIds(res?.data?.children);
                }

                setStep(3);
              }
            })
            .catch((e) => {
              const errorOptions = {
                title: "Ошибка подключения предложения",
                content: "Что-то пошло не так... ",
              };
              // @ts-ignore
              const errorMessage = e?.response?.data.message;
              if (errorMessage) {
                errorOptions.content = errorMessage;
              }
              Modal.error(errorOptions);
            })
            .finally(() => {
              setIsProductCreating(false);
            });
        });
        break;
      case 3:
        formCreateInvoicing.submit();
        break;
      default:
        break;
    }
  };

  const prevStep: IProductCreateContext["prevStep"] = () => {
    switch (step) {
      case 0:
        history.push(`/accounts/${accountId}/products`);
        break;
      case 1:
        offerListProps.onResetSearch();
        setStep(0);
        break;
      case 2:
        dispatch(offeringActions.cleanOffer());
        dispatch(offeringActions.cleanOfferHierarchy());
        dispatch(offeringActions.cleanHierarchyOfferList());
        if (savedStepSecondOnDateValue) {
          offerListProps.onChangeSearchValue({ onDate: savedStepSecondOnDateValue });
        }
        setStep(1);
        break;
      case 3:
        setStep(2);
        break;
      default:
        break;
    }
  };

  const nextBtnTitle = useMemo((): string => {
    switch (step) {
      case 0:
        return "Далее";
      case 1:
        return "Далее";
      case 2:
        return "Создать";
      default:
        return "";
    }
  }, [step]);

  const nextBtnActive = useMemo((): boolean => {
    switch (step) {
      case 0:
        return Boolean(selectedAgreement);
      case 1:
        if (offerListProps.offerListSelectMode === "new") {
          return Boolean(offerListProps.searchListValues.serviceTypeId);
        }
        return Boolean(offerListProps.selectedOffer);
      case 2:
        return true;
      default:
        return false;
    }
  }, [
    step,
    selectedAgreement,
    offerListProps.offerListSelectMode,
    offerListProps.selectedOffer,
    offerListProps.searchListValues.serviceTypeId,
  ]);

  const nextBtnTooltip = useMemo((): string => {
    if (nextBtnActive) {
      return "";
    }
    switch (step) {
      case 0:
        return "Необходимо выбрать договор";
      case 1:
        if (offerListProps.offerListSelectMode === "new") {
          return "Необходимо выбрать тип продукта";
        }
        return "Необходимо выбрать предложение";
      default:
        return "";
    }
  }, [step, nextBtnActive, offerListProps.offerListSelectMode]);

  // Обновление ставки налога в serviceRate, при измененении: fromDate, serviceTypeId
  const updateTaxRate = useDebounce(() => {
    const serviceTypeId =
      offerListProps.searchListValues.serviceTypeId || tableData[0]?.offering.serviceTypeId;
    if (serviceTypeId && tableData[0]?.fromDate) {
      dispatch(
        fetchTax({
          serviceTypeId,
          amount: 1, // Нужна только ставка налога, поэтому передается 1
          amountType: "NET",
          onDate: toHttpFormatStartOfDay(tableData[0].fromDate),
        })
      );
    } else {
      dispatch(taxTsActions.cleanTax());
    }
  }, 300);

  // Запрос ставки налога в serviceRate, при изменении даты или типа продукта
  useEffect(() => {
    if (tableData[0]?.fromDate) {
      updateTaxRate();
    }
  }, [
    tableData[0]?.fromDate,
    offerListProps.searchListValues.serviceTypeId,
    tableData[0]?.offering.serviceTypeId,
  ]);

  const openOfferDetailsModal = (key: string) => {
    setActiveModal("offerDetails", { key });
  };

  const closeOfferDetailsModal = () => {
    setActiveModal("");
  };

  // В Каталоге Типов продуктов отобразить развернутым раздел, соответствующий группе Типов продуктов, к которому относится Договор
  const onChangeAgreement = (agreement: Agreement | undefined) => {
    if (agreement) {
      setSelectedAgreement(agreement);
      const { productId, id } = agreement;
      offerListProps.setCatalogExpandedKeys([`productId:${productId}`]);
      offerListProps.onChangeSearchValue({
        productId,
      });
    } else {
      setSelectedAgreement(undefined);
      offerListProps.setCatalogExpandedKeys(["productId:0"]);
      offerListProps.onChangeSearchValue({
        productId: undefined,
        agreementId: undefined,
      });
    }
  };

  const handleSubmitFormCreateInvoicing = (fields) => {
    const bill = {
      fromDate: fields?.fromDate ? toHttpFormatStartOfDay(fields?.fromDate) : undefined,
      periodFrom: toHttpFormatStartOfDay(fields?.period[0]),
      periodTo: toHttpFormatEndOfDay(fields?.period[1]),
      sendBill: fields.sendBill,
      postBill: false,
      reportId: fields.reportId,
      serviceIds: productIdsCreate,
      agreementId: fields.agreementId,
      formatType: fields.formatType,
    };

    // @ts-ignore
    dispatch(addBill(bill))
      .then(() => {
        history.push(`/accounts/${accountId}/bills`);

        dispatch(fetchPayStatus());
        dispatch(fetchBillStatus());
        dispatch(fetchBillType());
        dispatch(fetchServiceStatus());
        if (agreements.length !== 0) {
          const aggArray = new Set();
          agreements.map((item) => {
            aggArray.add(item.id);
          });
          dispatch((fetchBillsByAggId as any)({ aggId: [...aggArray] }));
        }
      })
      .catch(() => {
        Modal.error({ content: "Ошибка создания счета" });
      });
  };

  const getAgreementsInitialValue = () => {
    let agreementDefault = selectedAgreement?.id;

    if (agreements && agreements?.length === 1) {
      agreementDefault = (agreements as { id: number }[])[0]?.id;
    }

    return agreementDefault;
  };

  return (
    <div className="product-create-container">
      <div className="product-create-container__title">
        <Link
          to={`/accounts/${accountId}/products`}
          style={{ visibility: step > 0 ? "hidden" : "inherit" }}
        >
          <LeftOutlined />
        </Link>
        <h2>Подключить продукт</h2>
        <h3 style={{ margin: "0 10px 0 auto" }}>
          {selectedAgreement
            ? `Договор ${selectedAgreement.agreementNumber} от ${toDisplayFormat(
                selectedAgreement.fromDate
              )}`
            : ""}
        </h3>
      </div>
      <Divider style={{ margin: "10px 0 0 0" }} />
      <Row gutter={19} style={{ marginRight: "10px" }}>
        <Col flex="auto">
          <Steps size="small" current={step}>
            <Steps.Step title="Выбор договора" />
            <Steps.Step title="Выбор Предложения" />
            <Steps.Step title="Параметры Предложения" />
            <Steps.Step title="Создать Счет на предоплату" />
          </Steps>
        </Col>
        {step < 3 && (
          <Col>
            <Space size={20}>
              <Button className="product-create-container__stepBtn" onClick={prevStep}>
                Назад
              </Button>
              <Tooltip title={nextBtnTooltip}>
                <Button
                  className="product-create-container__stepBtn"
                  type="primary"
                  onClick={nextStep}
                  disabled={!nextBtnActive}
                  loading={isProductCreating}
                >
                  {nextBtnTitle}
                </Button>
              </Tooltip>
            </Space>
          </Col>
        )}
      </Row>
      {step !== 1 && <Divider style={{ marginTop: 0 }} />}
      {step === 0 && (
        <ProductSelectAgreement
          onChangeAgreement={onChangeAgreement}
          selectedAgreement={selectedAgreement}
        />
      )}
      {step === 1 && (
        <OfferList
          filterName="create-product"
          {...offerListProps}
          disabledSearchFields={["isStandalone", "offeringStatusId"]}
          selectedAgreement={selectedAgreement}
          disableOnDateFn={(date: Moment) => date.isBefore(selectedAgreement?.fromDate)}
        />
      )}
      {step === 2 && selectedAgreement && (
        <Form form={createForm} fields={formFields}>
          <ProductParams
            offer={offerListProps.offerListSelectMode === "new" ? emptyOffering : offer}
            offerListSelectMode={offerListProps.offerListSelectMode}
            offerHierarchy={offerHierarchy}
            treeExpandedKeys={treeExpandedKeys}
            setTreeExpandedKeys={setTreeExpandedKeys}
            tableData={tableData}
            onChangeTableData={onChangeTableData}
            agreement={selectedAgreement}
            openOfferDetailsModal={openOfferDetailsModal}
            onChangeTableOfferData={onChangeTableOfferData}
            addedOfferProductsParams={addedOfferProductsParams}
            setAddedOfferProductsParams={setAddedOfferProductsParams}
            setChangingRecordKey={setChangingRecordKey}
            treeDataFormat={treeDataFormat}
            setTreeDataFormat={setTreeDataFormat}
            getCurrentOfferHierarchy={getCurrentOfferHierarchy}
          />
          <FormInfo
            getFormValues={() => {
              return { formData: createForm.getFieldsValue(), tableData };
            }}
            getCreateBody={getProductCreatingBody}
          />
        </Form>
      )}
      {step === 3 && selectedAgreement && (
        <Form
          onFinish={handleSubmitFormCreateInvoicing}
          form={formCreateInvoicing}
          layout="horizontal"
          initialValues={{
            sendBill: true,
            formatType: "PDF",
            agreementId: getAgreementsInitialValue(),
          }}
        >
          <ProductsCreateInvoicing
            cancelText="Закрыть"
            productFromDate={tableData[0]?.fromDate}
            productToDate={tableData[0]?.toDate}
            formCreateInvoicing={formCreateInvoicing}
          />
        </Form>
      )}
      <ProductOfferDetailsModal
        visible={activeModal === "offerDetails"}
        offer={tableData.find(({ key }) => key === activeModalOptions?.key)?.offering}
        isRoot={
          tableData.find(({ key }) => key === activeModalOptions?.key)?.parentId === undefined
        }
        searchListValues={offerListProps.searchListValues}
        closeOfferDetailsModal={closeOfferDetailsModal}
        onChangeTableOfferData={onChangeTableOfferData}
        onChangeTableData={onChangeTableData}
        tableRecordKey={activeModalOptions?.key}
        newRuleCondition={newRuleCondition}
        getNewAggregationRule={getNewAggregationRule}
        offerListSelectMode={offerListProps.offerListSelectMode}
        selectedAgreement={selectedAgreement}
        setIsCreatedOfferValid={setIsCreatedOfferValid}
      />
    </div>
  );
};
